import {
  useGetAllDashboardsInfo,
  useGetDashboard,
  useUpdateDashboard,
} from "api/api-hooks/dashboards-hooks";
import { Dashboard } from "api/model/dashboard/Dashboard";
import { IDashboardComponent } from "api/model/dashboard/DashboardComponent";
import { DashboardTabModel } from "api/model/dashboard/DashboardTab";
import {
  easasErrorAlert,
  easasSuccessAlert,
} from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown, DropdownChangeParams } from "primereact/dropdown";
import React, { useState } from "react";
import { T } from "services/lang/LanguageService";
import useDashboardStore from "store/dashboard-store";
import { v4 as uuidv4 } from "uuid";

interface MoveGroupComponentProps {
  isMoveComponentVisible: boolean;
  setIsMoveComponentVisible: (isMoveComponentVisible: boolean) => void;
  component: IDashboardComponent;
  tabId: number;
  groupId: string;
  componentId: string;
  dashboardId: number;
}

const MoveGroupComponent = (props: MoveGroupComponentProps) => {
  const [selectedGroup, setSelectedGroup] = useState<string>("");
  const [selectedDashboardTabId, setSelectedDashboardTabId] = useState<number | null>(props.tabId);
  const [selectedDashboard, setSelectedDashboard] = useState<number | null>(props.dashboardId);
  const currentDashboard = useDashboardStore(state => state.dashboard);

  const { data: allDashboardsInfo } = useGetAllDashboardsInfo();
  const { data: selectedDashboardInfo } = useGetDashboard(selectedDashboard || 0, {
    enabled: selectedDashboard !== null,
  });

  const updateQuery = useUpdateDashboard({
    onSuccess: () => {
      easasSuccessAlert(T("alert.dashboard-updated"));
    },
    onFailure: (error: Error) => {
      easasErrorAlert(error.message);
    },
  });

  const currentJSDashboards = allDashboardsInfo?.filter(
    dashboard => dashboard.implementation === "JS"
  );

  const tabOptions =
    selectedDashboardInfo?.dashboardTabsJs.map((tab, index) => ({
      label: tab.title,
      value: index,
    })) || [];

  const groupOptions = selectedDashboardInfo?.dashboardTabsJs.find(
    (tab, index) => index === selectedDashboardTabId
  )?.componentGroups;

  const handleTabChange = (e: DropdownChangeParams) => {
    setSelectedDashboardTabId(e.value);
  };

  const updateGroupComponents = (
    groupId: string,
    components: IDashboardComponent[],
    dashboardInfo: Dashboard,
    selectedTabId: number
  ) => {
    return dashboardInfo?.dashboardTabsJs[selectedTabId]?.componentGroups.map(group => {
      if (group.id === groupId) {
        return { ...group, components };
      }
      return group;
    });
  };

  const removeComponentFromSource = () => {
    return currentDashboard?.dashboardTabsJs.map((tab, index) => {
      if (index === props.tabId) {
        return {
          ...tab,
          componentGroups: tab.componentGroups.map(group => {
            if (group.id === props.groupId) {
              return {
                ...group,
                components: group.components.filter(
                  component => component.id !== props.componentId
                ),
              };
            }
            return group;
          }),
        };
      }
      return tab;
    });
  };

  const addComponentToTarget = (dashboardInfo: Dashboard, tabId: number) => {
    if (selectedDashboardTabId !== null) {
      const componentToMove = {
        ...props.component,
        id: uuidv4(),
      };

      const updatedComponents = [
        ...(selectedDashboardInfo?.dashboardTabsJs[selectedDashboardTabId]?.componentGroups?.find(
          group => group.id === selectedGroup
        )?.components || []),
        componentToMove,
      ];

      return updateGroupComponents(selectedGroup, updatedComponents, dashboardInfo, tabId);
    }

    return null;
  };

  const updateSourceDashboard = () => {
    const componentToMove = {
      ...props.component,
      id: uuidv4(),
    };

    const removeFromSource = (currentTab: DashboardTabModel) => {
      return {
        ...currentTab,
        componentGroups: currentTab.componentGroups.map(group => {
          if (group.id === props.groupId) {
            return {
              ...group,
              components: group.components.filter(component => component.id !== props.componentId),
            };
          }
          return group;
        }),
      };
    };

    const addToTarget = (currentTab: DashboardTabModel) => {
      return {
        ...currentTab,
        componentGroups: currentTab.componentGroups.map(group => {
          if (group.id === selectedGroup) {
            return {
              ...group,
              components: [...group.components, componentToMove],
            };
          }
          return group;
        }),
      };
    };

    return currentDashboard?.dashboardTabsJs.map((tab, index) => {
      let updatedTab = tab;

      if (index === props.tabId) {
        updatedTab = removeFromSource(updatedTab);
      }

      if (index === selectedDashboardTabId) {
        updatedTab = addToTarget(updatedTab);
      }

      return updatedTab;
    });
  };

  const handleMoveToGroup = () => {
    if (selectedDashboard && selectedGroup && selectedDashboardTabId !== null) {
      const isSameDashboard = selectedDashboardInfo?.id === currentDashboard?.id;
      const updatedSourceDashboard = {
        ...currentDashboard,
        dashboardTabsJs: isSameDashboard ? updateSourceDashboard() : removeComponentFromSource(),
      };
      updateQuery.mutate(updatedSourceDashboard);

      if (!isSameDashboard) {
        const updatedTargetDashboard = {
          ...selectedDashboardInfo,
          dashboardTabsJs: selectedDashboardInfo?.dashboardTabsJs.map((tab, index) => {
            if (index === selectedDashboardTabId) {
              return {
                ...tab,
                componentGroups: addComponentToTarget(
                  selectedDashboardInfo,
                  selectedDashboardTabId
                ),
              };
            }
            return tab;
          }),
        };
        updateQuery.mutate(updatedTargetDashboard);
      }

      props.setIsMoveComponentVisible(false);
    }
  };
  return (
    <Dialog
      header={T("action.move-component")}
      visible={props.isMoveComponentVisible}
      onHide={() => props.setIsMoveComponentVisible(false)}
      draggable={false}
      blockScroll
      footer={
        <div>
          <Button
            label={T("word.cancel")}
            icon="pi pi-times"
            onClick={() => props.setIsMoveComponentVisible(false)}
          />
          <Button
            label={T("action.move")}
            icon="pi pi-check"
            onClick={handleMoveToGroup}
            disabled={!selectedGroup}
          />
        </div>
      }
    >
      <div className="flex flex-column gap-3">
        <div className="flex flex-column gap-2">
          <label htmlFor="dashboard">{T("action.select-dashboard")}</label>
          <Dropdown
            id="dashboard"
            value={selectedDashboard}
            options={currentJSDashboards}
            filter
            onChange={e => setSelectedDashboard(e.value)}
            optionLabel="title"
            optionValue="id"
            placeholder={T("action.select-dashboard")}
          />
        </div>
        <div className="flex flex-column gap-2">
          <label htmlFor="tab">{T("action.select-tab")}</label>
          <Dropdown
            id="tab"
            value={selectedDashboardTabId}
            options={tabOptions}
            filter
            onChange={handleTabChange}
            optionLabel="label"
            optionValue="value"
            placeholder={T("action.select-tab")}
            disabled={tabOptions.length === 0}
          />
        </div>
        <div className="flex flex-column gap-2">
          <label htmlFor="currentGroups">{T("action.select-group")}:</label>
          <Dropdown
            id="currentGroups"
            value={selectedGroup}
            options={groupOptions?.map((group, index) => ({
              label: group.title || `${T("word.group")} ${index + 1}`,
              value: group.id,
            }))}
            onChange={e => setSelectedGroup(e.value)}
            placeholder={T("action.select-group")}
            disabled={selectedDashboard === null}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default MoveGroupComponent;
